import Meta from "@/components/meta";
import Navbar from "@/components/navbar";
import Header, {
  postHeroImageDimensions,
  postHeroImageSize,
  postSocialImageUrl,
} from "@/components/blog/header";
import PreviewBanner from "@/components/blog/preview_banner";
import TOC from "@/components/toc";
import Footer from "@/components/footer";
import Demobar from "@/components/demobar";
import components from "@/lib/mdx";

import { serialize } from "next-mdx-remote/serialize";
import { MDXRemote } from "next-mdx-remote";
import {
  getAllPostSlugs,
  getPostBySlug,
  getPostTOC,
  Post,
} from "@/lib/blogApi";

export default function Page({
  preview,
  post,
}: {
  post: Post;
  preview: boolean;
}) {
  return (
    <>
      <Meta
        title={post.title}
        name="Watershed Blog"
        description={post.excerpt}
        image={
          post.socialImg?.asset || post.heroImg?.asset
            ? postSocialImageUrl(post.socialImg, post.heroImg)
            : undefined
        }
      >
        {!post.socialImg?.asset && post.heroImg?.asset && (
          <>
            <meta
              key="og_img_width"
              property="og:image:width"
              content={postHeroImageDimensions[0].toString()}
            />
            <meta
              key="og_img_height"
              property="og:image:height"
              content={postHeroImageDimensions[1].toString()}
            />
          </>
        )}
        {(post.socialImg?.alt ?? post.heroImg?.alt) && (
          <meta
            key="og_img_alt"
            property="og:image:alt"
            content={post.socialImg?.alt ?? post.heroImg?.alt}
          />
        )}
        {post.videoDirectUrl && (
          <>
            <meta key="tw_card" name="twitter:card" content="player" />
            <meta
              key="tw_player"
              name="twitter:player"
              content={post.videoDirectUrl}
            />
            <meta
              key="og_video"
              property="og:video"
              content={post.videoDirectUrl}
            />
            <meta
              key="og_video_url"
              property="og:video:secure_url"
              content={post.videoDirectUrl}
            />
            <meta
              key="og_video_type"
              property="og:video:type"
              content="video/mp4"
            />
            <meta
              key="og_video_width"
              property="og:video:width"
              content="1920"
            />
            <meta
              key="og_video_height"
              property="og:video:height"
              content="1080"
            />
            <meta key="tw_video_width" name="tw:player:width" content="1920" />
            <meta
              key="tw_video_height"
              name="tw:player:height"
              content="1080"
            />
          </>
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "BlogPosting",
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `https://watershed.com/blog/${post.slug}`,
              },
              image: post.heroImg
                ? {
                    "@type": "ImageObject",
                    url: postSocialImageUrl(post.socialImg, post.heroImg),
                    description: post.heroImg.alt,
                    ...postHeroImageSize,
                  }
                : undefined,
              author: {
                "@type": "Person",
                name: "Watershed team",
              },
              datePublished: post.date,
              dateModified: post.updatedDate ?? post.date,
              headline: post.title,
              description: post.excerpt,
              url: `https://watershed.com/blog/${post.slug}`,
              publisher: {
                "@type": "Organization",
                name: "Watershed",
                url: "https://watershed.com",
                logo: {
                  "@type": "ImageObject",
                  url: "https://watershed.com/favicon.svg",
                },
              },
            }),
          }}
        />
      </Meta>
      <Navbar />
      {preview && post._id.startsWith("drafts.") && <PreviewBanner />}
      <main className={post.toc ? "with-toc" : ""}>
        <article className="blog-post">
          <Header post={post} heading="h1" />
          <MDXRemote {...post.contentSource} components={components} />
        </article>
        {Array.isArray(post.toc) && <TOC items={post.toc} />}
      </main>
      <div className="container">
        {post.slug !== "watershed-launch" && <Demobar narrow={!post.toc} />}
      </div>
      <Footer />
      <style jsx>{`
        article :global(strong) {
          color: #222c49;
        }
        @media (min-width: 820px) {
          .with-toc {
            display: grid;
            grid-gap: 24px;
            grid-template-columns: 2fr 1fr;
            align-items: start;
            padding: 24px 16px;
          }
        }
      `}</style>
    </>
  );
}

export async function getStaticProps({ params, preview = false }) {
  const post = await getPostBySlug(params.slug, preview);

  if (!post?.title) {
    return { notFound: true };
  }

  const toc = post.toc ? getPostTOC(post.contentText) : false;
  const contentSource = await serialize(post.contentText);
  delete post.contentText;

  return {
    props: {
      post: {
        ...post,
        toc,
        contentSource,
      },
      preview,
    },
  };
}

export async function getStaticPaths() {
  const paths = await getAllPostSlugs();
  return { paths, fallback: "blocking" };
}
